import { useState } from 'react'

import { Box } from '@factorialco/deprecated-design-system'
import { PayrollIntegrationsA3innuvaIncidenceDocument } from 'generated/resources/graphql'

import Callout from 'modules/core/components/Callout'
import useAsyncAction from 'modules/core/hooks/useAsyncAction'
import { useEffect } from 'modules/core/hooks/useEffect'
import { useQuery } from 'modules/core/hooks/useQuery'
import { NetworkError } from 'modules/core/lib/errors'
import i18n from 'modules/core/lib/i18n'

import { useBookkeeperView } from 'modules/bookkeepers/lib/bookkeeperViewContext'
import type IncidenceDetail from 'modules/bookkeepers/store/models/IncidenceDetail'
import integrationCodes from 'modules/marketplace/lib/integrationCodes'
import { INCIDENCE_TYPES } from 'modules/payroll_bundle/lib/Incidences/Types'
import PayrollIntegration, {
  PayrollIntegrationType,
} from 'modules/payroll_integrations/lib/catalog'
import type {
  Sync,
  SyncEmployeeCode,
} from 'modules/payroll_integrations_a3innuva/lib/IncidenceSync/store'
import incidenceSync, {
  Status,
} from 'modules/payroll_integrations_a3innuva/lib/IncidenceSync/store'
import authService, {
  Status as OauthStatus,
} from 'modules/payroll_integrations_a3innuva/lib/OauthService'

import Address from './Address'
import Bank from './Bank'
import Contract from './Contract'
import { CostCenter } from './CostCenter'
import Email from './Email'
import EmployeeCodeNotSynced from './EmployeeCodeNotSynced'
import Errored from './Errored'
import Gender from './Gender'
import Hiring from './Hiring'
import Id from './Id'
import Leave from './Leave'
import Name from './Name'
import Nationality from './Nationality'
import NotAuthenticated from './NotAuthenticated'
import PhoneNumber from './PhoneNumber'
import Termination from './Termination'
import Workplace from './Workplace'

interface Props {
  incidenceType: keyof typeof INCIDENCE_TYPES
  incidenceDetail: IncidenceDetail
  companyId?: number
  employeeId?: number | null
}

const selectComponent = (type: keyof typeof INCIDENCE_TYPES | null) => {
  switch (type) {
    case INCIDENCE_TYPES.name:
      return Name
    case INCIDENCE_TYPES.hiring:
      return Hiring
    case INCIDENCE_TYPES.contract:
      return Contract
    case INCIDENCE_TYPES.id:
      return Id
    case INCIDENCE_TYPES.bank:
      return Bank
    case INCIDENCE_TYPES.cost_center:
      return CostCenter
    case INCIDENCE_TYPES.termination:
      return Termination
    case INCIDENCE_TYPES.sick:
    case INCIDENCE_TYPES.parental:
    case INCIDENCE_TYPES.custom_leave:
      return Leave
    case INCIDENCE_TYPES.phone_number:
      return PhoneNumber
    case INCIDENCE_TYPES.gender:
      return Gender
    case INCIDENCE_TYPES.nationality:
      return Nationality
    case INCIDENCE_TYPES.address:
      return Address
    case INCIDENCE_TYPES.legal_entity:
      return Workplace
    case INCIDENCE_TYPES.email:
      return Email
    default:
      return null
  }
}

const Incidence = ({ incidenceType, incidenceDetail, companyId, employeeId }: Props) => {
  const incidenceId = incidenceDetail.attributes.incidenceId
  const Component = selectComponent(incidenceType)
  const [oauthStatus, setOAuthStatus] = useState<OauthStatus | null>(null)
  const [incidenceStatus, setIncidenceStatus] = useState<Sync | null>(null)
  const [syncEmployeeCode, setSyncEmployeeCode] = useState<SyncEmployeeCode | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [reload, setReload] = useState(false)
  const isBookkeeperView = useBookkeeperView()
  const isUserAuthenticated = oauthStatus === OauthStatus.Authenticated
  const isHiringIncidence = incidenceType === INCIDENCE_TYPES.hiring
  const [a3innuvaInstalled, setA3innuvaInstalled] = useState<boolean>(false)
  const [payrollIntegration, setPayrollIntegration] = useState<PayrollIntegrationType | null>(null)

  const { data } = useQuery(PayrollIntegrationsA3innuvaIncidenceDocument, {
    variables: { companyId: companyId || 0, datoIds: [integrationCodes.a3innuva] },
    skip: companyId == null,
  })

  const integrationsData = data?.marketplace?.integrationsConnection?.nodes || []
  const companyInstallationsData = data?.marketplace?.companyInstallationsConnection?.nodes || []

  interface Integration {
    id: number
    integrationId: number
    formData?: string | null
  }
  interface Integrations {
    [key: string]: Integration
  }

  const integrationMap = new Map(
    companyInstallationsData.map((integration) => [integration.integrationId, integration])
  )

  const installedIntegrations = integrationsData.reduce((acc: Integrations, current) => {
    if (!current.id) return acc
    const integration = integrationMap.get(current.id)
    if (!integration) return acc

    acc[current.name.toLocaleLowerCase()] = integration
    return acc
  }, {})

  // Hook to confirm if A3innuva integration is installed
  useEffect(() => {
    if (installedIntegrations.hasOwnProperty('a3innuva nómina')) {
      setA3innuvaInstalled(true)
    }
  }, [installedIntegrations.hasOwnProperty('a3innuva nómina')])

  // Hook to handle payroll integration
  useEffect(() => {
    if (a3innuvaInstalled) {
      setPayrollIntegration(PayrollIntegration.a3innuva)
    }
  }, [a3innuvaInstalled])

  // Hook to handle OAuth and incidence status
  useEffect(() => {
    if (!a3innuvaInstalled || Component === null) return

    const checkStatus = async () => {
      const currentOauthStatus = await authService.getStatus(
        { companyId: companyId },
        isBookkeeperView
      )
      setOAuthStatus(currentOauthStatus)

      if (currentOauthStatus === OauthStatus.NotAvailable) return

      if (payrollIntegration === null) return

      const currentIncidencestatus = await incidenceSync.status({
        incidenceId,
        // TODO: make generic for other API-based integrations
        isBookkeeperView: isBookkeeperView,
        integration: payrollIntegration,
      })
      setIncidenceStatus(currentIncidencestatus)

      if (currentIncidencestatus.unavailable_fields?.includes('employee_code')) {
        if (companyId == null || employeeId == null) return

        const syncEmployeeCodeResult = await incidenceSync.syncEmployeeCode({
          companyId,
          employeeId,
          isBookkeeperView: isBookkeeperView,
        })
        setSyncEmployeeCode(syncEmployeeCodeResult)

        if (syncEmployeeCodeResult.synced) setReload(!reload)
      }
    }
    checkStatus()
  }, [
    Component,
    incidenceId,
    reload,
    a3innuvaInstalled,
    payrollIntegration,
    companyId,
    employeeId,
    isBookkeeperView,
  ])

  const { running: syncing, performAction: onSync } = useAsyncAction(
    async () => {
      try {
        if (payrollIntegration === null) return

        if (!isUserAuthenticated && !isHiringIncidence) return
        const sync = await incidenceSync.sync({
          incidenceId,
          isBookkeeperView: isBookkeeperView,
          integration: payrollIntegration,
        })

        setIncidenceStatus(sync)
        setError(null)
      } catch (err: unknown) {
        let message: string
        if (err instanceof NetworkError) {
          message = ''
        } else if (err instanceof Error) {
          message = err.message
        } else {
          message = JSON.stringify(err)
        }

        setError(message)
        throw err
      }
    },
    {
      successMessage: i18n.t('payroll_integrations.incidences.a3innuva.sync.success_message'),
    }
  )
  const unavailableFields = incidenceStatus?.unavailable_fields ?? []
  const jobPostionDefaultValueWarningField = incidenceStatus?.default_value

  const jobPostionA3CodeWarningField = unavailableFields.find(
    (field) => field === 'job_position_a3_code'
  )

  if (Component === null) return null

  // TODO: we should check feature flags instead,
  // but we currently don't support multi-company feature flags
  if (oauthStatus === OauthStatus.NotAvailable) return null

  if (error !== null)
    return (
      <Box paddingTop="s24" paddingLeft="s24" paddingRight="s40">
        <Errored error={error} syncing={syncing} onSync={onSync} />
      </Box>
    )

  // For Hiring incidences we want to show the status of an incidence even before the user being logged in
  if (incidenceStatus && incidenceStatus.status !== Status.Available && isHiringIncidence)
    return (
      <Box paddingTop="s24" paddingLeft="s24" paddingRight="s40">
        <Component sync={incidenceStatus} syncing={syncing} onSync={onSync} />
      </Box>
    )

  if (oauthStatus === OauthStatus.NotAuthenticated)
    return (
      <Box paddingTop="s24" paddingLeft="s24" paddingRight="s40">
        <NotAuthenticated companyId={companyId} />
      </Box>
    )

  if (
    (syncEmployeeCode && !syncEmployeeCode.synced) ||
    (!(incidenceStatus === null) && incidenceStatus.unavailable_fields?.includes('employee_code'))
  )
    return (
      <Box paddingTop="s24" paddingLeft="s24" paddingRight="s40">
        <EmployeeCodeNotSynced setReload={setReload} reload={reload} />
      </Box>
    )

  if (incidenceStatus === null) return null

  return (
    <>
      <Box paddingTop="s24" paddingLeft="s24" paddingRight="s40">
        {jobPostionDefaultValueWarningField && !jobPostionA3CodeWarningField && (
          <Callout
            type="warning"
            title={i18n.t('payroll_integrations.incidences.a3innuva.default_value_warning.title')}
            description={i18n.t(
              'payroll_integrations.incidences.a3innuva.default_value_warning.message'
            )}
          />
        )}
      </Box>
      <Box paddingTop="s24" paddingLeft="s24" paddingRight="s40">
        <Component sync={incidenceStatus} syncing={syncing} onSync={onSync} />
      </Box>
    </>
  )
}

export default Incidence
